import React, {useContext, useEffect, useState} from 'react';
import QuickReportSummaryCard from "./QuickReportSummaryCard";
import axios from "@common/axios";
import dayjs from "dayjs";
import CircularLoader from "@common/CircularLoader";
import {UserContext} from "@context/UserContext";

const QuickActivityReportWrapper = () => {
    const [allReports, setAllReports] = useState([]);
    const [quickReports, setQuickReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const { user } = useContext(UserContext);

    const loadClassSummaryReports = async () => {
        setLoading(true);
        const end = dayjs();
        const start = end.subtract(6, 'day');

        const isUSLocale = user.locale === 'en_US';

        const day = end.date().toString().padStart(2, '0');
        const month = (end.month() + 1).toString().padStart(2, '0');
        const year = end.year();
        const formattedEnd = isUSLocale ? `${month}/${day}/${year}` : `${day}/${month}/${year}`;

        const startDay = start.date().toString().padStart(2, '0');
        const startMonth = (start.month() + 1).toString().padStart(2, '0');
        const startYear = start.year();
        const formattedStart = isUSLocale ? `${startMonth}/${startDay}/${startYear}` : `${startDay}/${startMonth}/${startYear}`;

        try {
            const response = await axios.get('/web/ui/report/teacher/activity/classSummaryReport', {
                params: {
                    startSearch: formattedStart,
                    endSearch: formattedEnd,
                    searchPeriod: 'DATE_RANGE'
                }
            });

            const data = response.data;
            setAllReports(data);
            setLoading(false);
        } catch (error) {
            console.error('Failed to load teacher classes:', error);
            setLoading(false);
        }
    };


    const processClassReports = (allReports) => {
        const data = allReports.reduce((acc, report) => {
            const { classId, className } = report;
            const reportData = report?.report?.reportData || [];

            if (!Array.isArray(reportData) || reportData.length === 0) {
                return acc;
            }

            const studentsWithActivities = reportData.map(student => {
                const {
                    countExercises = 0,
                    countQuizzes = 0,
                    countScorchers = 0,
                    countTests = 0,
                    countWalkthroughs = 0,
                    countSessions = 0,
                    name = 'Unnamed'
                } = student;

                const activityTotal = countExercises + countQuizzes + countScorchers + countTests + countWalkthroughs;

                const [last, first] = name.split(',').map(s => s.trim());
                const formattedName = first && last ? `${first} ${last.charAt(0)}.` : name;

                return {
                    name: formattedName,
                    activities: activityTotal,
                    sessions: countSessions
                };
            });

            const totalActivities = studentsWithActivities.reduce((sum, s) => sum + s.activities, 0);
            const totalSessions = studentsWithActivities.reduce((sum, s) => sum + s.sessions, 0);
            const studentCount = studentsWithActivities.length;

            const avgActivities = studentCount > 0 ? Math.ceil(totalActivities / studentCount) : 0;
            const avgSessions = studentCount > 0 ? Math.ceil(totalSessions / studentCount) : 0;

            const leaderboard = studentsWithActivities
                .sort((a, b) => b.activities - a.activities)
                .slice(0, 3)
                .map(s => ({ name: s.name, activities: s.activities }));

            acc.push({
                classId,
                className,
                classAvgData: {
                    avgSessions,
                    avgActivities
                },
                leaderboard
            });

            return acc;
        }, []);
        setQuickReports(data);
    };


    useEffect(() => {
        loadClassSummaryReports();
    },[])

    useEffect(() => {
        if(allReports && allReports.length > 0){
            processClassReports(allReports);
        } else {
            setQuickReports([]);
        }
    },[allReports])

    return (
        <div
            className={'ch-quick-activity-reports-wrapper'}
            role="region"
            aria-label="Quick activity reports"
            aria-roledescription="carousel"
            aria-busy={loading}
            aria-live="polite"
        >
            {loading && <CircularLoader pageCentered={false}/> }
            {!loading && quickReports.length > 0 && quickReports.map((quickReport) => (
                <QuickReportSummaryCard
                    key={quickReport.classId}
                    classId={quickReport.classId}
                    className={quickReport.className}
                    avgActivityCount={quickReport.classAvgData.avgActivities}
                    avgSessionsCount={quickReport.classAvgData.avgSessions}
                    leaderboard={quickReport.leaderboard}
                />
            ))}
            {!loading && quickReports.length === 0 &&
                <p className={'ch-warning-text'}>You're not a teacher on any classes, or your classes have no student activity in the last 7 days.</p>
            }
        </div>
    );
};

export default QuickActivityReportWrapper;